import React, {
  useState,
  memo,
  useCallback,
  useEffect,
  createContext,
} from 'react';
import { Form, Spin } from 'antd';
import { v4 as uuidv4 } from 'uuid';

import { ANIMATION_TYPES } from './config';
import FormRow from './row';
import { Loading } from '@components';
import Header from './header';
import ManageMAdd from './add-type-modal';
import '../../../styles/modules/project/integrationHub/manageForm.scss';
import { addDefaultKey } from './utils';
import store from '@flux/store';
import { renderCells } from './cell';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  getScrollableAncestors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

export const ConfigContext = createContext([]);

const getSortableContainer = () =>
  document.querySelector('.visible-form')?.querySelector?.('.form-list-body');

const AddBtn = (props) => {
  const { onClick, isLoading, text, onUploadPdfClick } = props;
  if (props.configs?.addModuleConfig) {
    return <ManageMAdd {...props} />;
  }
  return typeof text === 'object' ? (
    <div className="integration-nav-add-btn">
      <span
        className="appkiticon icon-circle-plus-fill a-font-24"
        onClick={!isLoading ? onClick : null}
      />
      <span className="text" onClick={!isLoading ? onClick : null}>
        {text[0]}
      </span>
      <span className="hiphen">or</span>
      <span
        className="appkiticon icon-circle-plus-fill a-font-24"
        onClick={!isLoading ? onUploadPdfClick : null}
      />
      <span className="text" onClick={!isLoading ? onUploadPdfClick : null}>
        {text[1]}
      </span>
    </div>
  ) : (
    <div
      className="integration-nav-add-btn"
      onClick={!isLoading ? onClick : null}
    >
      <span className="appkiticon icon-circle-plus-fill a-font-24" />
      <span className="text">{text}</span>
    </div>
  );
};

// ✅ Fixed RenderFieldItems with all required props
const RenderFieldItems = ({
  fields = [],
  activeRowKey,
  toggleActiveRowKey,
  handleRowDelete,
  deleteAnimationTrigger,
  name,
  animation,
  clearAnimation,
  onDragHandlerClick,
  formInstance,
  shouldHasExtraForm,
  refreshCount,
  projHomeExtraForm,
  multiSectionExtraForm,
  formChange,
  // ✅ Add these missing props
  ...restProps
}) => {
  // ✅ Create proper sortable items with unique IDs
  const sortableItems = fields.map((field, index) => ({
    ...field,
    sortableId: `${name}-${field.key}-${index}`, // Ensure unique ID
  }));

  return (
    <SortableContext
      items={sortableItems.map((item) => item.sortableId)}
      strategy={verticalListSortingStrategy}
    >
      <div className="ModuleList-nav">
        {sortableItems.map((field, index) => {
          let hasExtraForm = false;
          if (shouldHasExtraForm?.key && shouldHasExtraForm.has) {
            hasExtraForm = shouldHasExtraForm.has([
              name,
              index,
              shouldHasExtraForm.key,
            ]);
          }
          return (
            <FormRow
              collection={name}
              key={field.sortableId} // ✅ Use consistent key
              sortableId={field.sortableId} // ✅ Pass sortable ID
              index={index}
              rowKey={field.key}
              isActive={index === activeRowKey}
              field={field}
              name={name}
              animation={animation}
              onClick={toggleActiveRowKey}
              onRowDelete={deleteAnimationTrigger}
              afterDeleteAnimation={handleRowDelete}
              clearAnimation={clearAnimation}
              onDragHandlerClick={onDragHandlerClick}
              formInstance={formInstance}
              formChange={formChange}
              extraForm={hasExtraForm ? shouldHasExtraForm?.formConfig : null}
              refreshCount={refreshCount}
              projHomeExtraForm={projHomeExtraForm}
              multiSectionExtraForm={multiSectionExtraForm}
              {...restProps}
            />
          );
        })}
      </div>
    </SortableContext>
  );
};

const renderFileds =
  ({
    formInstance: form,
    name,
    setAnimation,
    registerFieldsMethods,
    getDefaultRowData,
    addBtnText,
    maxRow,
    setShowUpload,
    moduleType,
    showSpn,
    formChange = () => {},
    sensors,
    onDragStart,
    onDragEnd,
    onDragCancel,
    activeId,
    ...props
  }) =>
  (fields, { add, remove, move }) => {
    form.__moduleListRemove = remove;
    form.__moduleListFormChange = formChange;
    registerFieldsMethods &&
      registerFieldsMethods({
        remove,
        move,
        add,
      });

    const currentRowCount = (form.getFieldValue(name) || []).length;
    let hideAddBtn = false;

    if (maxRow > -1 && currentRowCount >= maxRow) {
      hideAddBtn = true;
    }

    const storeData = store.getState();
    const _data = {
      project_id: storeData.common.currentId,
      uid: storeData.login.userInfo.guid,
      full_name: `${storeData.login.userInfo.given_name} ${storeData.login.userInfo.family_name}`,
    };

    const handleRowDelete = (fieldKey, animatingRowIndex) => {
      const indexToRemove =
        typeof animatingRowIndex === 'number' && animatingRowIndex >= 0
          ? animatingRowIndex
          : fields.findIndex((f) => f.key === fieldKey);
      if (indexToRemove >= 0) {
        remove(indexToRemove);
      }
      formChange();
    };

    const deleteAnimationTrigger = (fieldKey) => {
      setAnimation({
        id: `${name}${fieldKey}`,
        action: ANIMATION_TYPES.DELETE,
      });
    };

    const handleAddRow = async (type) => {
      const animation = {
        id: `${name}${currentRowCount}`,
        action: ANIMATION_TYPES.ADD,
      };
      let defaultRow =
        getDefaultRowData?.(
          await addDefaultKey(
            type,
            props?.configs?.addModuleConfig?.isModule,
            props?.ratioNeedFullSize,
          ),
        ) || {};

      const isTextArray = props.formData && props.formData.length > 0;
      if (isTextArray) {
        const id = uuidv4();
        defaultRow = {
          id,
          name: {
            text: '',
            id,
          },
          activities: {
            data: [''],
            id,
          },
        };
      }

      add(defaultRow);
      formChange();
      if (isTextArray) {
        props.formData.push(defaultRow);
      }
      setAnimation(animation);
      setTimeout(() => {
        getSortableContainer() && getSortableContainer().scroll(0, 100000);
      }, 100);
    };

    return (
      <>
        <div className="form-list-content">
          {fields.length > 0 ? (
            // <DndContext
            //   sensors={sensors}
            //   collisionDetection={closestCenter}
            //   onDragEnd={onDragEnd}
            //   onDragStart={onDragStart}
            //   onDragCancel={onDragCancel}
            //   autoScroll={{
            //     enabled: true,
            //     threshold: {
            //       x: 0.2,
            //       y: 0.2,
            //     },
            //     acceleration: 10,
            //   }}
            // >
            <RenderFieldItems
              fields={fields}
              activeRowKey={props.activeRowKey} // ✅ Pass missing props
              toggleActiveRowKey={props.toggleActiveRowKey} // ✅ Pass missing props
              deleteAnimationTrigger={deleteAnimationTrigger}
              handleRowDelete={handleRowDelete}
              name={name}
              animation={props.animation} // ✅ Pass missing props
              clearAnimation={props.clearAnimation} // ✅ Pass missing props
              onDragHandlerClick={props.onDragHandlerClick} // ✅ Pass missing props
              formInstance={form}
              shouldHasExtraForm={props.shouldHasExtraForm} // ✅ Pass missing props
              refreshCount={props.refreshCount} // ✅ Pass missing props
              projHomeExtraForm={props.projHomeExtraForm} // ✅ Pass missing props
              multiSectionExtraForm={props.multiSectionExtraForm} // ✅ Pass missing props
              formChange={formChange}
              activeId={activeId}
              {...props} // ✅ Pass all other props
            />
          ) : (
            // </DndContext>
            <div className="no-data">No Data</div>
          )}
        </div>
        {!hideAddBtn && (
          <AddBtn
            moduleType={moduleType}
            text={addBtnText}
            onClick={handleAddRow}
            configs={props.configs}
            showSpn={showSpn}
            onUploadPdfClick={() => {
              handleAddRow('pdf');
            }}
          />
        )}
      </>
    );
  };

function FormList(props) {
  const [activeRowKey, toggleActiveRowKey] = useState(-1);
  const [animation, setAnimation] = useState({});
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor), // ✅ No activation constraints = immediate drag
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const {
    name,
    isActive,
    isLoading,
    columns,
    defaultColumns,
    configs = {},
    formData,
  } = props;

  const handleCancelActiveRow = () => {
    toggleActiveRowKey(-1);
  };

  const clearAnimation = useCallback(() => {
    setAnimation({
      id: -1,
    });
    toggleActiveRowKey(null);
  }, []);

  const handleDragStart = useCallback((event) => {
    // console.log('🟢 DRAG START:', event.active.id);
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      // console.log('🔴 DRAG END:', active?.id, 'over:', over?.id);

      setActiveId(null);

      // ✅ Clear all drag handle highlights immediately
      const clearDragHandleHighlights = () => {
        // Remove any active/hover states from drag handles
        document.querySelectorAll('.drag-handle').forEach((handle) => {
          handle.classList.remove('active', 'dragging', 'highlighted');
          handle.style.backgroundColor = '';
          handle.style.borderColor = '';
          handle.style.transform = '';
          handle.blur(); // Remove focus
        });

        // Remove any drag-related classes from rows
        document.querySelectorAll('.dragable-table-form-row').forEach((row) => {
          row.classList.remove('drag-active', 'drag-source', 'drag-target');
        });
      };

      // Clear highlights immediately
      clearDragHandleHighlights();

      // ✅ Clear drop indicators after 500ms
      setTimeout(() => {
        document
          .querySelectorAll('.drop-indicator-above, .drop-indicator-below')
          .forEach((el) => {
            el.classList.remove('drop-indicator-above', 'drop-indicator-below');
          });
      }, 500);

      if (!over || active.id === over.id) {
        return;
      }

      const fields = props.formInstance.getFieldValue(name) || [];

      const getFieldIndex = (sortableId) => {
        const parts = sortableId.split('-');
        return parseInt(parts[parts.length - 1]);
      };

      const oldIndex = getFieldIndex(active.id);
      const newIndex = getFieldIndex(over.id);

      if (oldIndex !== newIndex) {
        const newFields = arrayMove(fields, oldIndex, newIndex);
        props.formInstance.setFieldsValue({
          [name]: newFields,
        });
        props.formChange?.();

        // ✅ After form update, clear any remaining highlights
        setTimeout(() => {
          clearDragHandleHighlights();
        }, 100);
      }
    },
    [name, props.formInstance, props.formChange],
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);

    // ✅ Clear highlights on drag cancel too
    document.querySelectorAll('.drag-handle').forEach((handle) => {
      handle.classList.remove('active', 'dragging', 'highlighted');
      handle.style.backgroundColor = '';
      handle.style.borderColor = '';
      handle.style.transform = '';
      handle.blur();
    });

    // Clear drop indicators
    document
      .querySelectorAll('.drop-indicator-above, .drop-indicator-below')
      .forEach((el) => {
        el.classList.remove('drop-indicator-above', 'drop-indicator-below');
      });
  }, []);

  // ✅ Correct activeItem calculation
  const activeItem = activeId
    ? (() => {
        const fields = props.formInstance.getFieldValue(name) || [];
        const parts = activeId.split('-');
        const index = parseInt(parts[parts.length - 1]);
        return fields[index];
      })()
    : null;

  const fieldProps = {
    activeRowKey,
    toggleActiveRowKey,
    name,
    animation,
    setAnimation,
    clearAnimation,
    onDragHandlerClick: handleCancelActiveRow,
    sensors,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
    activeId,
    ...props,
  };

  useEffect(() => {
    if (!isActive) {
      toggleActiveRowKey(-1);
    }
  }, [isActive]);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;

    // Clear any existing indicators
    document
      .querySelectorAll('.drop-indicator-above, .drop-indicator-below')
      .forEach((el) => {
        el.classList.remove('drop-indicator-above', 'drop-indicator-below');
      });

    if (!over || active.id === over.id) return;

    // Find the element being dragged over
    const overElement = document.querySelector(
      `[data-sortable-id="${over.id}"]`,
    );
    if (overElement) {
      const activeIndex = parseInt(active.id.split('-').pop());
      const overIndex = parseInt(over.id.split('-').pop());

      // Add visual indicator based on drag direction
      if (activeIndex < overIndex) {
        overElement.classList.add('drop-indicator-below');
      } else {
        overElement.classList.add('drop-indicator-above');
      }
    }
  }, []);

  return (
    <ConfigContext.Provider
      value={{ columns, defaultColumns, ...configs, formData }}
    >
      {/* ✅ Move DndContext to wrap everything */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        onDragOver={handleDragOver}
      >
        <div className={isActive ? 'visible-form' : 'invisible-form'}>
          <Spin spinning={isLoading} indicator={<Loading />}>
            <Header />
            <div className="form-list-body">
              <Form.List name={name}>{renderFileds(fieldProps)}</Form.List>
            </div>
          </Spin>
        </div>

        {/* ✅ DragOverlay at the very end, outside all containers */}
        <DragOverlay dropAnimation={null}>
          {activeId ? (
            <div className="drag-overlay-clone">
              {(() => {
                const fields = props.formInstance.getFieldValue(name) || [];
                const parts = activeId.split('-');
                const index = parseInt(parts[parts.length - 1]);

                // ✅ Get the actual field data correctly
                const actualField = fields[index];
                // console.log('ACTUALFIELD :', actualField);

                // console.log('🎯 DragOverlay data:', {
                //   activeId,
                //   index,
                //   actualField,
                //   allFields: fields,
                // });

                if (!actualField) {
                  return (
                    <div
                      style={{
                        background: 'orange',
                        color: 'white',
                        padding: '15px',
                        borderRadius: '4px',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                      }}
                    >
                      Moving row {index + 1}...
                    </div>
                  );
                }

                // Get the correct columns (your existing logic is fine)
                let _columns = columns || [];
                const customizeModule =
                  props.formInstance.getFieldValue('customizeModule');
                const projectHomeTmplt =
                  props.formInstance.getFieldValue('projectHomeTmplt');

                if (
                  customizeModule &&
                  customizeModule[index]?.type === 'default'
                ) {
                  _columns = defaultColumns || [];
                } else if (
                  projectHomeTmplt &&
                  projectHomeTmplt[index]?.type === 'default'
                ) {
                  _columns = defaultColumns || [];
                }

                // ✅ Create proper mock field with correct structure
                const mockField = {
                  key: index, // or actualField.key if it exists
                  name: index,
                  fieldKey: index,
                  module: actualField.module,
                  // ✅ Make sure to include all the data from actualField
                  ...actualField,
                };

                // console.log('🔧 Mock field for overlay:', mockField);

                try {
                  const cells = _columns.map(
                    renderCells({
                      ...props,
                      field: mockField, // ✅ Now has correct data
                      index,
                      collection: name,
                      rowKey: index,
                      formInstance: props.formInstance, // ✅ Make sure form instance is passed
                      // ✅ Pass the actual field values
                      fieldValue: actualField,
                      module: actualField.module,
                      ...configs,
                    }),
                  );

                  return (
                    <div
                      className="dragable-table-form-row drag-overlay-row"
                      style={{
                        background: 'white',
                        border: '2px solid #1890ff',
                        borderRadius: '6px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                        transform: 'scale(1.02)',
                        opacity: 0.95,
                        minWidth: '300px',
                        zIndex: 9999, // ✅ Ensure it's on top
                      }}
                    >
                      {cells}
                    </div>
                  );
                } catch (error) {
                  console.error('Error rendering drag overlay cells:', error);
                  console.error('Error details:', {
                    mockField,
                    _columns,
                    props,
                  });
                  return (
                    <div
                      style={{
                        background: '#ff4d4f',
                        color: 'white',
                        padding: '15px',
                        borderRadius: '4px',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                      }}
                    >
                      Error rendering overlay for row {index + 1}
                    </div>
                  );
                }
              })()}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </ConfigContext.Provider>
  );
}

const SortableForm = (props) => <FormList {...props} />;

const CombinedForm = (props) => <SortableForm {...props} />;

export default memo(CombinedForm);
